import React, {useRef, useState} from 'react';
import {CallAsset} from '@store/call-card/CallCard.types';
import cn from 'classnames';
import './CallMediaPlayer.sass';
import {DoubleLeftChevronIcon} from '@components/icon/DoubleLeftChevronIcon';
import {DoubleRightChevronIcon} from '@components/icon/DoubleRightChevronIcon';
import {PlayIcon} from '@components/icon/PlayIcon';
import {PauseIcon} from '@components/icon/PauseIcon';
import {WindowRestoreIcon} from '@components/icon/WindowRestoreIcon';
import {LinkIcon} from '@components/icon/LinkIcon';
import IconButton from '@material-ui/core/IconButton';
import {PlayerControls} from '@components/call-card/PlayerControls';
import {PlayerRef} from '@components/call-card/Player.utils';
import Loading from 'components/pure/utils/Loading';
import {MaximizeIcon} from '@components/icon/MaximizeIcon';
import {MinimizeIcon} from '@components/icon/MinimizeIcon';
import {XMarkLargeIcon} from '@components/icon/XMarkLargeIcon';

interface CallMediaPlayerProps {
  setPlayerRef: (ref: PlayerRef) => void;
  callAsset: CallAsset | undefined;
  autoPopout?: boolean;
}

export const CallMediaPlayer: React.FC<CallMediaPlayerProps> = ({
  autoPopout,
  callAsset,
  setPlayerRef,
}) => {
  const playerRef = useRef<PlayerRef>(null);
  const [popout, setPopout] = useState(autoPopout);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const [position, setPosition] = useState<'left' | 'right'>('right');
  const player = playerRef.current;

  if (!callAsset) return <Loading />;
  if (callAsset.type === 'MISSING_ASSET') {
    return (
      <div className="call-asset-missing-error">
        We were unable to find a recording for this call
      </div>
    );
  }

  const src = `/assets/download/${callAsset.id}`;
  const mediaElement =
    callAsset.type === 'OUTPUT_AUDIO' ? (
      <audio
        onLoadedMetadata={() => setCanPlay(true)}
        src={src}
        ref={r => {
          playerRef.current = r;
          setPlayerRef(r);
        }}
      />
    ) : (
      <>
        <video
          webkit-playsinline="true"
          playsInline
          onLoadedMetadata={() => setCanPlay(true)}
          src={src}
          ref={r => {
            playerRef.current = r;
            setPlayerRef(r);
          }}
        />
        <VideoControlOverlay
          setHidden={setHidden}
          setPopout={setPopout}
          setPosition={setPosition}
          setExpanded={setExpanded}
          player={player}
          popout={popout}
          position={position}
          expanded={expanded}
        />
      </>
    );

  return (
    <div className="call-media-player">
      {!canPlay && <Loading />}
      <div className={cn('video-wrapper', position, {popout, hidden, canPlay, expanded})}>
        {mediaElement}
      </div>
      <PlayerControls
        showVideo={() => setHidden(false)}
        videoHidden={hidden}
        playerRef={playerRef.current}
      />
    </div>
  );
};

function VideoControlOverlay({
  setHidden,
  player,
  setPopout,
  popout,
  setPosition,
  position,
  setExpanded,
  expanded,
}) {
  return (
    <div className="control-overlay">
      <IconButton className="close-button" onClick={() => setHidden(true)}>
        <XMarkLargeIcon tooltip="Hide" />
      </IconButton>
      {player?.paused ? (
        <IconButton className="play-button" onClick={() => player.play()}>
          <PlayIcon tooltip="Play" />
        </IconButton>
      ) : (
        <IconButton className="pause-button" onClick={() => player?.pause()}>
          <PauseIcon tooltip="Pause" />
        </IconButton>
      )}
      {popout ? (
        <>
          <IconButton className="restore-button" onClick={() => setPopout(false)}>
            <WindowRestoreIcon tooltip="Restore" />
          </IconButton>
          {position === 'right' ? (
            <IconButton className="move-left-button" onClick={() => setPosition('left')}>
              <DoubleLeftChevronIcon tooltip="Move to left" />
            </IconButton>
          ) : (
            <IconButton className="move-right-button" onClick={() => setPosition('right')}>
              <DoubleRightChevronIcon tooltip="Move to right" />
            </IconButton>
          )}
        </>
      ) : (
        <>
          {expanded ? (
            <IconButton className="minimize-button" onClick={() => setExpanded(false)}>
              <MinimizeIcon tooltip="Minimize" />
            </IconButton>
          ) : (
            <IconButton className="expand-button" onClick={() => setExpanded(true)}>
              <MaximizeIcon tooltip="Expand" />
            </IconButton>
          )}
          <IconButton className="popout-button" onClick={() => setPopout(true)}>
            <LinkIcon tooltip="Miniplayer" />
          </IconButton>
        </>
      )}
    </div>
  );
}
